// HeaderlessConversationView.jsx - Clean conversation view without headers for tabs

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import LogoLoader from './LogoLoader';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import ChatMessage from './organisms/ChatMessage';
import SignaturePad from './SignaturePad';
import { useAuth0 } from '@auth0/auth0-react';
import { onPrivateMessageReceived, sendPrivateMessage } from '../socket';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import Attachments from '../Images/attachment-icon.svg';
import { useToast } from '../Context/ToastContext';

// Date Separator Component
const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    margin: 16px 0;
  }
`;

const DateSeparatorLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, transparent, #e6ecf6, transparent);
`;

const DateSeparatorText = styled.div`
  padding: 6px 16px;
  background-color: #e6ecf6;
  border-radius: 20px;
  font-size: 12px;
  color: #455776;
  font-weight: 500;
  margin: 0 12px;
  backdrop-filter: blur(10px);
  border: 1px solid #e0e8f5;
  
  @media (max-width: 768px) {
    font-size: 11px;
    padding: 5px 12px;
    margin: 0 8px;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
  min-height: 0;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  overflow: hidden;
  position: relative;
`;

const ExpertHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid rgba(91, 97, 234, 0.12);
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  z-index: 10;
  
  @media (max-width: 768px) {
    padding: 12px 15px;
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    border-bottom: 1px solid #e0e8f5;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const AdvisorAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.bgColor || '#335cf4'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 500;
  font-size: 14px;
  margin-right: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const AdvisorInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const AdvisorName = styled.div`
  color: #192947;
  font-weight: 500;
  font-size: 15px;
  margin-bottom: 2px;
`;

const OnlineStatus = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${props => props.isOnline ? '#0f8f63' : '#65748d'};
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${props => props.isOnline ? '#12a571' : '#e6ecf6'};
    margin-right: 6px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  padding-bottom: 100px;
  overflow-y: auto;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 15px;
    padding-bottom: 100px;
  }
`;

const SendMessageWrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  border: 1px solid #e0e8f5;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%);
  padding: 8px 18px;
  border-radius: 14px;
  z-index: 10;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(39, 68, 131, 0.105);
  
  &:focus-within {
    border-color: rgba(51, 92, 244, 0.4);
    box-shadow: 0 8px 32px rgba(51, 92, 244, 0.15);
  }
  
  ${props => props.$hasFile && `
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    &:hover, &:focus-within {
      background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    }
  `}
  
  @media (max-width: 768px) {
    position: fixed;
    bottom: calc(75px + env(safe-area-inset-bottom, 0px));
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% - 30px);
    z-index: 1000;
    border-radius: 12px;
  }
`;

const Input = styled.input`
  flex: 1;
  border: none;
  background-color: transparent;
  color: #192947;
  margin-left: 0.8rem;
  margin-right: 0.8rem;
  height: 40px;
  padding: 0 8px;
  font-size: 15px;
  font-weight: 400;

  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: #7b8aa3;
    font-weight: 300;
  }
`;

const IconWrapper = styled.button`
  background: none;
  border: none;
  padding: 8px;
  margin: 0 2px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  img {
    filter: brightness(0) saturate(100%) invert(44%) sepia(31%) saturate(334%) hue-rotate(179deg) brightness(93%) contrast(86%);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(51, 92, 244, 0.15) 0%, rgba(51, 92, 244, 0.1) 100%);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FilePreview = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #f5f8fd;
  color: #192947;
  padding: 3px 8px;
  margin-right: 8px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #e0e8f5;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #33445e;
  margin-left: 8px;
  cursor: pointer;
  padding: 0 4px;
  font-size: 16px;
  
  &:hover {
    color: #192947;
  }
`;

const EmojiPickerContainer = styled.div`
  position: absolute;
  bottom: 100px;
  right: 20px;
  z-index: 1000;

  .emoji-picker-react {
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%) !important;
    border-color: #e0e8f5 !important;

    .emoji-group:before {
      background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%) !important;
      color: #192947 !important;
    }

    .emoji-search {
      background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%) !important;
      border-color: #e0e8f5 !important;
      color: #192947 !important;
    }

    .emoji-categories {
      background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%) !important;
    }

    .emoji-group {
      background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%) !important;
    }
  }
`;

const MicIconWrapper = styled(IconWrapper)`
  position: relative;
`;

const RecordingIndicator = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #ff4136;
  top: 0;
  right: 0;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% {
      transform: scale(0.8);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.8;
    }
    100% {
      transform: scale(0.8);
      opacity: 1;
    }
  }
`;

const RecordingTimer = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #455776;
  white-space: nowrap;
`;

const TranscriptionLoadingIndicator = styled.div`
  position: absolute;
  top: -25px;
  left: 10%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #455776;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  padding: 3px 8px;
  border-radius: 12px;
  gap: 6px;
`;

const SendIconWrapper = styled(IconWrapper)`
  position: absolute;
  right: 15px;
  img, svg {
    filter: invert(43%) sepia(57%) saturate(1752%) hue-rotate(190deg) brightness(92%) contrast(98%);
  }
  
  &:hover {
    background-color: rgba(51, 92, 244, 0.15);
  }
`;

const NoConversationMessage = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #455776;
  padding: 40px;
  text-align: center;
  gap: 20px;
  
  .empty-icon {
    width: 100px;
    height: 100px;
    background: linear-gradient(135deg, rgba(51, 92, 244, 0.15) 0%, rgba(51, 92, 244, 0.05) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 42px;
    border: 1px solid rgba(51, 92, 244, 0.2);
    margin-bottom: 8px;
    animation: float 3s ease-in-out infinite;
  }
  
  .empty-title {
    font-size: 20px;
    font-weight: 600;
    color: #192947;
    letter-spacing: 0.3px;
  }
  
  .empty-subtitle {
    font-size: 14px;
    color: #7b8aa3;
    max-width: 280px;
    line-height: 1.6;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    
    .empty-icon {
      width: 80px;
      height: 80px;
      font-size: 36px;
    }
    
    .empty-title {
      font-size: 18px;
    }
    
    .empty-subtitle {
      font-size: 13px;
    }
  }
  font-size: 1.2em;
`;

const HeaderlessConversationView = ({ conversation, expertId, setConversation }) => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { user, isAuthenticated } = useAuth0();
  const { showToast } = useToast();
  const [imageError, setImageError] = useState(false);

  // Client attaches a file to a requested document (from a document_request card).
  const handleDocumentAttach = async (requestId, item, file) => {
    const form = new FormData();
    form.append('file', file);
    form.append('requestId', requestId || '');
    form.append('requirementKey', item.requirementKey);
    if (item.kind) form.append('kind', item.kind);
    await authenticatedAxios.post(`/api/requirements/${conversation.threadId}/attach`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    showToast?.(`Attached "${item.name}"`, 'success');
  };

  // State to hold the expert profile image if fetched
  const [expertProfileImage, setExpertProfileImage] = useState(null);

  // Local states
  const [message, setMessage] = useState('');
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [isHelpDialogVisible, setIsHelpDialogVisible] = useState(false);

  // Store file info (single file example: name, type, base64 data)
  const [selectedFile, setSelectedFile] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const uploadRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);

  // Add these new state variables and refs inside the ConversationView component
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionStatus, setTranscriptionStatus] = useState('');
  const [audioChunks, setAudioChunks] = useState([]);
  const timerRef = useRef(null);

  // Add a state variable to track the MIME type
  const [recordingMimeType, setRecordingMimeType] = useState('audio/webm');

  // Signature request state
  const [signatureRequest, setSignatureRequest] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);

  /**
   * Toggle emoji picker visibility
   */
  const toggleEmojiPicker = () => {
    setEmojiPickerVisible((prev) => !prev);
  };

  /**
   * Add clicked emoji to the message input
   */
  const emojiSelected = (emojiData) => {
    try {
      let emojiToInsert = '';

      // @emoji-mart/react v1.x returns an object with native property
      if (emojiData && typeof emojiData === 'object') {
        if (emojiData.native) {
          emojiToInsert = emojiData.native;
        } else if (emojiData.unified) {
          // Try to convert unified code to actual emoji
          const unified = emojiData.unified.split('-').map(u => parseInt(u, 16));
          emojiToInsert = String.fromCodePoint(...unified);
        } else if (emojiData.id || emojiData.colons) {
          // Try fallback to id or colons (though these aren't ideal)
          emojiToInsert = emojiData.id || emojiData.colons || '';
        }
      }
      // Direct string value 
      else if (typeof emojiData === 'string') {
        emojiToInsert = emojiData;
      }

      // Only update if we have something to insert
      if (emojiToInsert) {
        setMessage(prevMessage => prevMessage + emojiToInsert);

        // Focus input after adding emoji
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 10);

        // Close emoji picker after selection only on desktop
        if (window.innerWidth > 768) {
          setEmojiPickerVisible(false);
        }
      } else {
        console.warn('Could not extract emoji character from data:', emojiData);
      }
    } catch (error) {
      console.error('Error handling emoji selection:', error);
    }
  };

  /**
   * Scroll to the bottom of the MessagesContainer
   */
  const scrollToBottom = () => {
    const isMobile = window.innerWidth <= 768;

    // Use direct scrollTop manipulation for more reliable scrolling
    if (messagesContainerRef.current) {
      const scrollHeight = messagesContainerRef.current.scrollHeight;
      const clientHeight = messagesContainerRef.current.clientHeight;
      const maxScrollTop = scrollHeight - clientHeight;
      
      messagesContainerRef.current.scrollTop = maxScrollTop;
      
      // For mobile, do an additional scroll after a short delay
      if (isMobile) {
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const scrollHeight = messagesContainerRef.current.scrollHeight;
            const clientHeight = messagesContainerRef.current.clientHeight;
            const maxScrollTop = scrollHeight - clientHeight;
            messagesContainerRef.current.scrollTop = maxScrollTop;
          }
        }, 100);
        
        // And one more for good measure on mobile
        setTimeout(() => {
          if (messagesContainerRef.current) {
            const scrollHeight = messagesContainerRef.current.scrollHeight;
            const clientHeight = messagesContainerRef.current.clientHeight;
            const maxScrollTop = scrollHeight - clientHeight;
            messagesContainerRef.current.scrollTop = maxScrollTop;
          }
        }, 300);
      }
    }
    
    // Also try scrollIntoView as a fallback
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'auto', 
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  /**
   * Listen for private messages from the server
   */
  useEffect(() => {
    const handlePrivateMessage = (data) => {
      if (data.threadId != conversation?.threadId) {
        return;
      }

      console.log('Received private message:', data);

      // Structure the incoming message to match ChatMessage's expected format
      const newMessage = {
        type: data.type,
        userId: data.userId,
        sender: data.username,
        content: data.content,
        receiverId: data.receiverId,
        time: new Date().toISOString(),
        fileName: data.fileName,
        fileType: data.fileType,
        fileData: data.fileData
      };

      // Update the conversation with the new message
      const updatedConversation = {
        ...conversation,
        messages: [...conversation.messages, newMessage],
      };
      setConversation(updatedConversation);
    };

    const unsubscribe = onPrivateMessageReceived(handlePrivateMessage);
    return () => {
      unsubscribe && unsubscribe();
    };
  }, [conversation, setConversation]);

  /**
   * Initial scroll when conversation loads
   */
  useEffect(() => {
    if (conversation?.threadId && !hasInitiallyScrolled) {
      setHasInitiallyScrolled(true);
      // Multiple attempts to ensure initial scroll works
      requestAnimationFrame(scrollToBottom);
      setTimeout(scrollToBottom, 100);
      setTimeout(scrollToBottom, 300);
      setTimeout(scrollToBottom, 500);
    }
  }, [conversation?.threadId, hasInitiallyScrolled]);

  /**
   * Reset initial scroll flag when conversation changes
   */
  useEffect(() => {
    setHasInitiallyScrolled(false);
  }, [conversation?.threadId]);

  /**
   * Once conversation messages change, scroll to bottom
   */
  useEffect(() => {
    if (conversation?.messages) {
      // Use requestAnimationFrame for better timing
      requestAnimationFrame(() => {
        scrollToBottom();
      });

      // Short timeout to ensure DOM is updated before scrolling
      setTimeout(scrollToBottom, 50);

      // Additional scroll for mobile devices to handle delayed rendering
      if (window.innerWidth <= 768) {
        setTimeout(scrollToBottom, 300);
        setTimeout(scrollToBottom, 500);

        // Also ensure input is visible
        setTimeout(ensureInputVisible, 350);
      }
    }
  }, [conversation?.messages]);

  /**
   * Force scroll to bottom after sending a message
   */
  useEffect(() => {
    if (loadingMessage === false && message === '') {
      // Message was just sent, force scroll
      scrollToBottom();
    }
  }, [loadingMessage, message]);

  /**
   * Handle text input change
   */
  const handleInputChange = (event) => {
    setMessage(event.target.value);
  };

  /**
   * Ensure the input field is fully visible
   */
  const ensureInputVisible = () => {
    // Scroll the container to ensure the input is visible
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.scrollIntoView({ block: 'end', behavior: 'auto' });

        // Focus the input after ensuring it's visible, but only on desktop
        const isMobile = window.innerWidth <= 768;
        if (!isMobile) {
          inputRef.current.focus();
        }
      }, 300);
    }
  };

  /**
   * Send the message (with optional file)
   */
  const handleSendMessage = async () => {
    if (!message.trim() && !selectedFile) {
      return; // Don't send empty text if no file attached
    }

    setLoadingMessage(true);

    // Local version of the new message (for immediate UI feedback)
    const newMessage = {
      id: Date.now(), // Generate a unique ID, e.g., using UUID
      type: 'user', // Set type based on whether a file is attached
      content: selectedFile ? `data:${selectedFile.type};base64,${selectedFile.data}` : message, // If file is attached, content can be empty or a description
      fileName: selectedFile ? selectedFile.name : '',
      fileType: selectedFile ? selectedFile.type : '',
      time: new Date().toISOString(),
      sender: user.sub, // Adjust based on your logic
    };

    if (conversation && isAuthenticated) {
      // Determine the receiver (expert vs. conversation data)
      let userReceiver = '';
      if (expertId) {
        userReceiver = expertId;
      } else {
        // fallback: find the expert from the conversation
        const foundExpert = conversation.users.find((x) => x.type === 'expert');
        userReceiver = foundExpert ? foundExpert.id : '';
      }

      // Build the payload for Socket.IO
      const payload = {
        id: newMessage.id,
        content: message,
        threadId: conversation.threadId,
        receiverId: userReceiver,
        senderId: user?.sub,
        type: 'user',
        time: new Date().toISOString(),
      };

      // If a file is selected, add its info
      if (selectedFile) {
        payload.fileName = selectedFile.name;
        payload.fileType = selectedFile.type;
        payload.fileData = selectedFile.data;
      }

      // Send message to the server
      sendPrivateMessage(payload);

      // Update local conversation
      const updatedConversation = {
        ...conversation,
        messages: [...conversation.messages, newMessage],
      };
      setConversation(updatedConversation);
    }

    // Clear message input & file selection
    setMessage('');
    setSelectedFile(null);
    setLoadingMessage(false);

    // Force scroll to bottom after sending
    setTimeout(scrollToBottom, 100);

    // Ensure input field is visible
    ensureInputVisible();
  };

  /**
   * When user presses Enter, send message if not loading
   */
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loadingMessage) {
      handleSendMessage();
    }
  };

  /**
   * Open file dialog
   */
  const uploadDoc = () => {
    if (uploadRef.current) {
      uploadRef.current.click();
    }
  };

  /**
   * Handle opening signature pad when user clicks on signature request
   */
  const handleOpenSignaturePad = (data) => {
    setSignatureRequest(data);
    setShowSignaturePad(true);
  };

  /**
   * Handle signature submission
   */
  const handleSignatureSubmit = async (signatureData) => {
    setIsSubmittingSignature(true);
    try {
      const response = await authenticatedAxios.post('/api/signatures/submit', {
        ...signatureData,
        odId: user?.sub,
        userName: user?.name || 'User',
        userEmail: user?.email
      });

      if (response.data.success) {
        // Update the message in local state to show signed status
        const updatedMessages = conversation.messages.map(msg => {
          if (msg.type === 'signature_request') {
            try {
              const msgData = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
              if (msgData.requestCode === signatureRequest?.requestCode) {
                return {
                  ...msg,
                  content: JSON.stringify({
                    ...msgData,
                    status: 'signed',
                    signedAt: new Date().toISOString()
                  })
                };
              }
            } catch (e) {
              // Ignore parsing errors
            }
          }
          return msg;
        });
        
        setConversation(prev => ({ ...prev, messages: updatedMessages }));
        setShowSignaturePad(false);
        setSignatureRequest(null);
        showToast('Signature submitted successfully!', 'success');
      } else {
        showToast('Failed to submit signature', 'error');
      }
    } catch (error) {
      console.error('Error submitting signature:', error);
      showToast('Error submitting signature', 'error');
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  /**
   * Handle signature decline
   */
  const handleSignatureDecline = async () => {
    try {
      await authenticatedAxios.post('/api/signatures/decline', {
        requestCode: signatureRequest?.requestCode,
        reason: 'User declined'
      });

      // Update the message in local state
      const updatedMessages = conversation.messages.map(msg => {
        if (msg.type === 'signature_request') {
          try {
            const msgData = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
            if (msgData.requestCode === signatureRequest?.requestCode) {
              return {
                ...msg,
                content: JSON.stringify({
                  ...msgData,
                  status: 'declined'
                })
              };
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
        return msg;
      });
      
      setConversation(prev => ({ ...prev, messages: updatedMessages }));
      showToast('Signature request declined', 'info');
    } catch (error) {
      console.error('Error declining signature:', error);
    }
    setShowSignaturePad(false);
    setSignatureRequest(null);
  };

  /**
   * File selection handler -> reads file as Base64
   */
  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (!files || !files.length) {
      return;
    }

    const file = files[0];
    try {
      const base64String = await readFileAsBase64(file);
      setSelectedFile({
        name: file.name,
        type: file.type,
        data: base64String,
      });
    } catch (error) {
      console.error('Error reading file:', error);
    }

    // Reset the file input so the same file can be selected again
    event.target.value = null;
  };

  /**
   * Helper to read File as base64
   */
  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // "data:[mime/type];base64,iVBORw0KGgo..."
        const resultStr = reader.result?.toString() || '';
        const base64 = resultStr.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handle clicks outside emoji picker to close it
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      console.log("Click detected, checking if outside emoji picker");
      // Only close if clicking outside both the emoji picker and emoji button
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !event.target.closest('img[alt="emoji icon"]') &&
        !event.target.closest('[data-testid="emoji-picker"]')
      ) {
        console.log("Click was outside emoji picker, closing");
        setEmojiPickerVisible(false);
      } else {
        console.log("Click was inside emoji picker or button, keeping open");
      }
    };

    // Only add the listener if the picker is visible
    if (emojiPickerVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [emojiPickerVisible]);

  // Add a new useEffect for touch event detection
  useEffect(() => {
    // Function to check if user is at or near the bottom of the messages
    const isNearBottom = () => {
      if (messagesContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        // If scrolled to within 100px of the bottom, consider it "near bottom"
        return scrollHeight - scrollTop - clientHeight < 100;
      }
      return false;
    };

    // Handle touch end events on mobile
    const handleTouchEnd = () => {
      // If user was already near bottom, scroll to very bottom
      if (isNearBottom() && conversation?.messages?.length > 0) {
        setTimeout(scrollToBottom, 50);
      }
    };

    // Add touch event listener for mobile devices
    if (window.innerWidth <= 768 && messagesContainerRef.current) {
      messagesContainerRef.current.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      // Clean up event listener
      if (messagesContainerRef.current) {
        messagesContainerRef.current.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [conversation?.messages]);

  // Update the toggleRecording function to ensure audioChunks are cleared properly
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      if (mediaRecorder) {
        mediaRecorder.stop();
        clearInterval(timerRef.current);
        setRecordingTime(0);
      }
      setIsRecording(false);
      // Transcription will start automatically in the onstop handler
    } else {
      try {
        // Start recording
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 16000, // Whisper works well with 16kHz audio
            echoCancellation: true,
            noiseSuppression: true,
          }
        });

        // Clear previous chunks immediately when starting a new recording
        setAudioChunks([]);

        // Determine supported MIME types for better compatibility
        let mimeType = 'audio/webm';

        // Check if browser supports different formats
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
          mimeType = 'audio/ogg;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/mp4;codecs=mp4a.40.2')) {
          mimeType = 'audio/mp4;codecs=mp4a.40.2';
        }

        console.log(`Using MIME type: ${mimeType}`);
        setRecordingMimeType(mimeType);

        const recorder = new MediaRecorder(stream, {
          mimeType,
          audioBitsPerSecond: 128000
        });

        // Using a local array to collect chunks for this specific recording session
        const currentRecordingChunks = [];

        // Set up recording data handling
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            currentRecordingChunks.push(e.data);
            setAudioChunks(prev => [...prev, e.data]);
          }
        };

        // When recording stops, start transcription with the local chunks
        recorder.onstop = () => {
          // Use the local chunks array for this specific recording session
          transcribeAudio(stream, currentRecordingChunks);
        };

        // Start the recording with data available every 1 second
        recorder.start(1000);
        setMediaRecorder(recorder);
        setIsRecording(true);

        // Start the timer
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      } catch (error) {
        console.error("Error accessing microphone:", error);
        showToast("Could not access your microphone. Please check permissions and try again.", "error");
      }
    }
  };

  // Update the transcribeAudio function to accept specific chunks
  const transcribeAudio = async (stream, specificChunks = null) => {
    try {
      // Release the microphone
      stream.getTracks().forEach(track => track.stop());

      // Show loading indicator
      setIsTranscribing(true);
      setTranscriptionStatus('Processing audio');

      // Use the specific chunks passed to this function, or fall back to the state
      const chunksToProcess = specificChunks || audioChunks;

      // Log for debugging
      console.log(`Processing ${chunksToProcess.length} audio chunks`);

      // Create a blob from the audio chunks with the correct MIME type
      const audioBlob = new Blob(chunksToProcess, { type: recordingMimeType });

      // Get the file extension based on MIME type
      const getFileExtension = (mimeType) => {
        if (mimeType.includes('webm')) return 'webm';
        if (mimeType.includes('ogg')) return 'ogg';
        if (mimeType.includes('mp4')) return 'mp4';
        return 'webm'; // Default
      };

      const fileExtension = getFileExtension(recordingMimeType);
      console.log(`Using file extension: ${fileExtension}, Blob size: ${(audioBlob.size / 1024).toFixed(2)} KB`);

      // No need to check for minimum recording size - even short recordings are fine

      // Create FormData for the API call
      const formData = new FormData();
      formData.append('file', audioBlob, `recording_${Date.now()}.${fileExtension}`);

      setTranscriptionStatus('Sending to server');

      // Log request details for debugging
      console.log('Sending audio to backend for transcription');

      // Use the correct endpoint that matches your Python server
      const response = await authenticatedAxios.post('/api/process_audio', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Expert-ID': expertId || '', // Required for Whisper billing
        },
        timeout: 60000, // Increase timeout to 60 seconds for processing longer recordings
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (percentCompleted === 100) {
            setTranscriptionStatus('Transcribing with Whisper');
          }
        }
      });

      console.log('Received response from server:', response.data);

      // Check if we have a valid response
      if (!response.data || !response.data.transcription) {
        console.log('Response data structure:', response.data);
        throw new Error("No transcription returned from server");
      }

      // Get transcription from the response
      const transcribedText = response.data.transcription;

      // Check if the transcription is empty
      if (!transcribedText.trim()) {
        throw new Error("Couldn't hear anything. Please try again and speak clearly.");
      }

      // Add the transcribed text to the input
      setMessage(prev => prev + (prev.length > 0 ? ' ' : '') + transcribedText);

      // Focus the input for editing
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error("Error during transcription:", error);
      console.error("Error details:", error.response?.data || "No response data");

      // Provide user-friendly error messages based on the error type
      let errorMessage = "There was an error transcribing your audio. Please try again.";

      if (error.message.includes("hear anything")) {
        errorMessage = "Couldn't detect any speech. Please try again and speak clearly.";
      } else if (error.response && error.response.status === 413) {
        errorMessage = "Recording too long. Please try a shorter message.";
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = "Transcription timed out. Please try a shorter recording.";
      } else if (error.response && error.response.status === 500) {
        errorMessage = "Server error processing your audio. The team has been notified.";
        console.error("Server returned 500 error:", error.response?.data);
      }

      showToast(errorMessage, "error");
    } finally {
      // Hide loading indicator
      setIsTranscribing(false);
      setTranscriptionStatus('');
    }
  };

  // Format the recording time for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Add cleanup function to the useEffect
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      if (mediaRecorder && isRecording) {
        mediaRecorder.stop();
      }
    };
  }, [mediaRecorder, isRecording]);

  // Helper function to format date for separators
  const formatDateSeparator = (date) => {
    const today = new Date();
    const messageDate = new Date(date);
    const diffTime = today.getTime() - messageDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return messageDate.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      return messageDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Helper function to check if two dates are on the same day
  const isSameDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getDate() === d2.getDate() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getFullYear() === d2.getFullYear();
  };

  const conversationMessages = (conversation) => {
    const translatedMessages = conversation.messages.map(msg => {
      if (msg.translatedMessages && msg.type === 'expert') {
        return {
          ...msg,
          content: msg.translatedMessages.content
        }
      } else {
        return {
          ...msg,
          content: msg.content
        }
      }
    })
    
    const resolveSenderName = (msg) => {
      switch (msg.type) {
        case 'user':
          return (user?.name || user?.email || 'You');
        case 'expert':
          return (conversation?.name || conversation?.expert?.name || conversation?.expert?.fullName || conversation?.expertName || 'Adviser');
        case 'system':
          return 'iMavenAi';
        case 'gpt':
          return 'Eve virtual assistant';
        default:
          return (conversation?.name || 'Adviser');
      }
    }

    // Group messages with date separators
    const messagesWithSeparators = [];
    let lastDate = null;

    translatedMessages?.forEach((msg, index) => {
      const messageDate = msg.time;
      
      // Add date separator if this is the first message or date changed
      if (!lastDate || !isSameDay(lastDate, messageDate)) {
        messagesWithSeparators.push({
          type: 'date-separator',
          date: messageDate,
          key: `separator-${index}`
        });
        lastDate = messageDate;
      }
      
      // Add the message
      messagesWithSeparators.push({
        ...msg,
        key: `message-${index}`
      });
    });

    return <>
      {messagesWithSeparators?.map((item) => {
        if (item.type === 'date-separator') {
          return (
            <DateSeparator key={item.key}>
              <DateSeparatorLine />
              <DateSeparatorText>
                {formatDateSeparator(item.date)}
              </DateSeparatorText>
              <DateSeparatorLine />
            </DateSeparator>
          );
        } else {
          return (
            <ChatMessage 
              key={item.key} 
              message={item} 
              type={item.type} 
              timestamp={item.time} 
              senderName={resolveSenderName(item)}
              onSignatureClick={(data) => handleOpenSignaturePad({ ...data, requestCode: item.requestCode || data.requestCode })}
              onDocumentAttach={handleDocumentAttach}
            />
          );
        }
      })}
    </>
  }

  // Update the emoji picker position based on view
  const getEmojiPickerStyle = () => {
    if (window.innerWidth <= 480) {
      return {
        bottom: '60px',
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '101',
        maxWidth: '90vw'
      };
    } else if (window.innerWidth <= 768) {
      return {
        bottom: '80px',
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: '101',
        maxWidth: '90vw'
      };
    } else {
      // Updated desktop position for better visibility
      return {
        bottom: '70px',
        position: 'absolute',
        left: '15px',
        zIndex: '101',
        maxWidth: '320px'
      };
    }
  };

  if (!conversation) {
    return (
      <ContentContainer>
        <NoConversationMessage>
          <div className="empty-icon">💬</div>
          <div className="empty-title">Select a conversation</div>
          <div className="empty-subtitle">
            Choose an adviser from the list to start chatting and get expert guidance
          </div>
        </NoConversationMessage>
      </ContentContainer>
    );
  }

  // Helper function to generate avatar color from name
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 65%, 50%)`;
  };

  // Get advisor initials
  const getAdvisorInitials = () => {
    const name = conversation?.name || conversation?.expert?.name || 'A';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2);
  };

  return (
    <ContentContainer>
      {/* Expert Header with Advisor Info - visible on both desktop and mobile */}
      <ExpertHeader>
        <HeaderInfo>
          <AdvisorAvatar bgColor={stringToColor(conversation?.name || 'Advisor')}>
            {getAdvisorInitials()}
          </AdvisorAvatar>
          <AdvisorInfo>
            <AdvisorName>{conversation?.name || 'Advisor'}</AdvisorName>
            <OnlineStatus isOnline={conversation?.isOnline}>
              {conversation?.isOnline ? 'Online' : 'Offline'}
            </OnlineStatus>
          </AdvisorInfo>
        </HeaderInfo>
      </ExpertHeader>

      <MessagesContainer ref={messagesContainerRef}>
        {conversationMessages(conversation)}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {emojiPickerVisible && (
        <div
          ref={emojiPickerRef}
          style={getEmojiPickerStyle()}
          onClick={(e) => {
            // Stop propagation to prevent immediate closing
            e.stopPropagation();
            console.log("Clicked inside emoji picker");
          }}
        >
          <Picker
            data={data}
            onEmojiSelect={emojiSelected}
            theme="light"
            previewPosition="none"
            skinTonePosition="none"
            perLine={window.innerWidth <= 480 ? 6 : 9}
          />
        </div>
      )}

      <SendMessageWrapper $hasFile={selectedFile}>
        <input
          name="files"
          type="file"
          style={{ display: 'none' }}
          ref={uploadRef}
          accept=".jpg,.jpeg,.png,.gif,.bmp,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.html,.md,.csv,.json,.epub"
          multiple
          onChange={handleFileChange}
        />

        {/* Upload Icon */}
        <IconWrapper disabled={loadingMessage} onClick={uploadDoc}>
          <img
            src={Attachments}
            alt="upload icon"
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        </IconWrapper>

        {/* Emoji Icon */}
        <IconWrapper
          disabled={loadingMessage}
          onClick={toggleEmojiPicker}
          className="emoji-picker-toggle"
          data-testid="emoji-button"
        >
          <img
            src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgY2xhc3M9ImZlYXRoZXIgZmVhdGhlci1zbWlsZSI+PGNpcmNsZSBjeD0iMTIiIGN5PSIxMiIgcj0iMTAiPjwvY2lyY2xlPjxwYXRoIGQ9Ik04IDE0czEuNSAyIDQgMiA0LTIgNC0yIj48L3BhdGg+PGxpbmUgeDE9IjkiIHkxPSI5IiB4Mj0iOSIgeTI9IjkiPjwvbGluZT48bGluZSB4MT0iMTUiIHkxPSI5IiB4Mj0iMTUiIHkyPSI5Ij48L2xpbmU+PC9zdmc+"
            alt="emoji icon"
            style={{
              width: '20px',
              height: '20px'
            }}
          />
          {emojiPickerVisible && (
            <span style={{
              position: 'absolute',
              bottom: '-5px',
              right: '-5px',
              width: '8px',
              height: '8px',
              backgroundColor: '#335cf4',
              borderRadius: '50%'
            }} />
          )}
        </IconWrapper>

        {/* Microphone Icon */}
        {!message && !selectedFile && (
          <SendIconWrapper
            style={{ marginRight: '0px' }}
            disabled={loadingMessage || isTranscribing}
            onClick={toggleRecording}
          >
            <img
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgY2xhc3M9ImZlYXRoZXIgZmVhdGhlci1taWMiPjxwYXRoIGQ9Ik0xMiAyYTQgNCAwIDAgMSA0IDR2NGE0IDQgMCAwIDEtOCAwVjZhNCA0IDAgMCAxIDQtNHoiPjwvcGF0aD48cGF0aCBkPSJNMTkgMTB2MmE3IDcgMCAwIDEtMTQgMHYtMiI+PC9wYXRoPjxsaW5lIHgxPSIxMiIgeTE9IjE5IiB4Mj0iMTIiIHkyPSIyMiI+PC9saW5lPjxsaW5lIHgxPSI4IiB5MT0iMjIiIHgyPSIxNiIgeTI9IjIyIj48L2xpbmU+PC9zdmc+"
              alt="microphone icon"
              style={{
                width: '20px',
                height: '20px',
                filter: 'invert(43%) sepia(57%) saturate(1752%) hue-rotate(190deg) brightness(92%) contrast(98%)'
              }}
            />
            {isRecording && (
              <>
                <RecordingIndicator />
                <RecordingTimer>{formatTime(recordingTime)}</RecordingTimer>
              </>
            )}
            {isTranscribing && (
              <TranscriptionLoadingIndicator><LogoLoader compact size={14} label="Transcribing" />{transcriptionStatus || 'Transcribing'}</TranscriptionLoadingIndicator>
            )}
          </SendIconWrapper>
        )}

        {/* Text input */}
        <Input
          ref={inputRef}
          placeholder="Type your message..."
          type="text"
          value={message}
          onChange={handleInputChange}
          disabled={loadingMessage}
          onKeyDown={handleKeyPress}
        />

        {/* Show selected file preview */}
        {selectedFile && (
          <FilePreview>
            <span>{selectedFile.name}</span>
            <RemoveButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
              }}
            >
              ×
            </RemoveButton>
          </FilePreview>
        )}

        {/* Send Icon */}
        {(message || selectedFile) && (
          <SendIconWrapper
            style={{ marginRight: '0px' }}
            disabled={loadingMessage}
            onClick={handleSendMessage}
          >
            <img
              src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgY2xhc3M9ImZlYXRoZXIgZmVhdGhlci1zZW5kIj48bGluZSB4MT0iMjIiIHkxPSIyIiB4Mj0iMTEiIHkyPSIxMyI+PC9saW5lPjxwb2x5Z29uIHBvaW50cz0iMjIgMiAxNSAyMiAxMSAxMyAyIDkgMjIgMiI+PC9wb2x5Z29uPjwvc3ZnPg=="
              alt="send icon"
              style={{
                width: '20px',
                height: '20px'
              }}
            />
          </SendIconWrapper>
        )}
      </SendMessageWrapper>

      {/* Signature Pad Modal */}
      <SignaturePad
        isOpen={showSignaturePad}
        onClose={() => {
          setShowSignaturePad(false);
          setSignatureRequest(null);
        }}
        onSubmit={handleSignatureSubmit}
        onDecline={handleSignatureDecline}
        purpose={signatureRequest?.purpose || ''}
        description={signatureRequest?.description || ''}
        requestCode={signatureRequest?.requestCode || ''}
        isSubmitting={isSubmittingSignature}
      />
    </ContentContainer>
  );
};

export default HeaderlessConversationView; 